import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetNationalHolisticAnalyticsQuery,
  deriveRecordMetrics,
  mergeNationalAnalyticsResponses,
  TermFilter,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import { StatBox } from '../../../components/shared';

const COLORS = ['#4F46E5', '#6366F1', '#818CF8', '#A5B4FC'];

const PeerAnalysis: React.FC = () => {
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    if (!records.length) {
      return {
        avgPeer: 0,
        cooperative: 0,
        needsSupport: 0,
        chartData: [
          { metric: 'Cooperative', count: 0 },
          { metric: 'Needs Support', count: 0 },
        ],
        distribution: [
          { range: '90-100', count: 0 },
          { range: '70-89', count: 0 },
          { range: '50-69', count: 0 },
          { range: '<50', count: 0 },
        ],
      };
    }

    let totalPeer = 0;
    let cooperative = 0;
    let needsSupport = 0;

    let dist90_100 = 0;
    let dist70_89 = 0;
    let dist50_69 = 0;
    let distUnder50 = 0;

    records.forEach((record) => {
      const metrics = deriveRecordMetrics(record, term);
      totalPeer += metrics.peer;

      if (metrics.peer >= 70) {
        cooperative++;
      } else {
        needsSupport++;
      }

      const pScore = metrics.peer;
      if (pScore >= 90) dist90_100++;
      else if (pScore >= 70) dist70_89++;
      else if (pScore >= 50) dist50_69++;
      else distUnder50++;
    });

    const len = records.length;
    return {
      avgPeer: Math.round(totalPeer / len),
      cooperative,
      needsSupport,
      chartData: [
        { metric: 'Cooperative (>= 70)', count: cooperative },
        { metric: 'Needs Support (< 70)', count: needsSupport },
      ],
      distribution: [
        { range: '90-100', count: dist90_100 },
        { range: '70-89', count: dist70_89 },
        { range: '50-69', count: dist50_69 },
        { range: '<50', count: distUnder50 },
      ],
    };
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Peer Interaction Analysis</h1>
          <p className="text-sm text-gray-500">Aggregate peer interaction patterns across {records.length} students</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatBox label="Avg Peer Score" value={`${stats.avgPeer}`} color="text-indigo-600" large />
        <StatBox label="Cooperative" value={`${stats.cooperative}`} color="text-emerald-600" large />
        <StatBox label="Needs Support" value={`${stats.needsSupport}`} color="text-amber-600" large />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Cooperative vs Needs Support</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.chartData} barSize={56}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="metric" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                <Cell fill="#10B981" />
                <Cell fill="#F59E0B" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Score Distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.distribution} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="range" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stats.distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default PeerAnalysis;

